/**
 * Group Reviews API Service
 * Handles all group review operations via backend API
 */

import apiClient from './api.js';

class GroupReviewsService {
  
  /**
   * Submit a group review (fraud verdict)
   * @param {Object} reviewData - Review data
   * @param {string} reviewData.groupLink - Group Telegram link
   * @param {string} reviewData.groupName - Group display name
   * @param {string} reviewData.status - 'fake', 'suspicious', or 'genuine'
   * @param {string} reviewData.reason - Reason for the verdict
   * @param {string} reviewData.officerId - Officer email who made the review
   * @param {string} reviewData.source - Source of the review
   * @param {Array<string>} reviewData.scanReportIds - Optional: Related scan report IDs to update
   * @returns {Promise<Object>} - Review submission response
   */
  async submitReview(reviewData) {
    const officerId = encodeURIComponent((reviewData?.officerId || 'admin').toString());
    return await apiClient.post(`/group-reviews?officerId=${officerId}`, reviewData);
  }

  /**
   * Backward-compatible alias used by older pages.
   * @param {Object} reviewData - Review data
   * @returns {Promise<Object>} - Review submission response
   */
  async createReview(reviewData) {
    return await this.submitReview(reviewData);
  }

  async updateBlockedStatus({ groupLink, groupName, blockedStatus, officerId, scanReportIds }) {
    return await apiClient.patch('/group-reviews/blocked-status', {
      groupLink,
      groupName,
      blockedStatus,
      officerId,
      scanReportIds,
    });
  }

  /**
   * Get group reviews with filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.status - 'fake', 'genuine', or 'all'
   * @param {string} filters.officerId - Officer email filter
   * @param {string} filters.groupLink - Specific group filter
   * @param {string} filters.since - ISO timestamp to get reviews after
   * @param {string} filters.startDate - ISO start date filter
   * @param {string} filters.endDate - ISO end date filter
   * @param {number} filters.limit - Max results per page (default: 50)
   * @param {number} filters.offset - Skip N results for pagination
   * @returns {Promise<Object>} - Response with reviews array and metadata
   */
  async getReviews(filters = {}) {
    const params = {
      limit: 50,
      offset: 0,
      ...filters
    };

    return await apiClient.get('/group-reviews', params);
  }

  /**
   * Get all reviews (for FrudDB display)
   * @param {number} limit - Max results
   * @returns {Promise<Object>} - All recent reviews
   */
  async getAllReviews(limit = 100) {
    return await this.getReviews({
      status: 'all',
      limit
    });
  }

  /**
   * Get reviews by status
   * @param {string} status - 'fake', 'genuine'
   * @param {number} limit - Max results
   * @returns {Promise<Object>} - Filtered reviews
   */
  async getReviewsByStatus(status, limit = 100) {
    return await this.getReviews({
      status,
      limit
    });
  }

  /**
   * Get reviews for a specific group
   * @param {string} groupLink - Group Telegram link
   * @returns {Promise<Object>} - Group's review history
   */
  async getGroupReviews(groupLink) {
    return await this.getReviews({
      groupLink,
      limit: 50
    });
  }

  /**
   * Get reviews by officer
   * @param {string} officerId - Officer email
   * @param {string} status - Optional status filter
   * @param {number} limit - Max results
   * @returns {Promise<Object>} - Officer's reviews
   */
  async getReviewsByOfficer(officerId, status = 'all', limit = 50) {
    return await this.getReviews({
      officerId,
      status: status === 'all' ? undefined : status,
      limit
    });
  }

  /**
   * Get recent reviews since a timestamp
   * @param {string|Date} since - ISO timestamp or Date object
   * @param {number} limit - Max results
   * @returns {Promise<Object>} - Recent reviews
   */
  async getRecentReviews(since, limit = 50) {
    const sinceISO = since instanceof Date ? since.toISOString() : since;
    
    return await this.getReviews({
      since: sinceISO,
      limit
    });
  }

  /**
   * Check if a group has been reviewed
   * @param {string} groupLink - Group Telegram link
   * @returns {Promise<Object|null>} - Latest review for the group, or null
   */
  async getGroupStatus(groupLink) {
    const response = await this.getGroupReviews(groupLink);
    
    if (response.success && response.reviews.length > 0) {
      // Return the most recent review
      return response.reviews[0];
    }
    
    return null;
  }

  /**
   * Get fraud statistics
   * @returns {Promise<Object>} - Statistics about fake vs genuine reviews
   */
  async getStats() {
    try {
      // Get all reviews and calculate stats client-side
      const allReviews = await this.getAllReviews(1000);
      
      const stats = {
        total: allReviews.reviews.length,
        fake: 0,
        suspicious: 0,
        genuine: 0
      };

      allReviews.reviews.forEach(review => {
        if (review.status === 'fake') {
          stats.fake++;
        } else if (review.status === 'suspicious') {
          stats.suspicious++;
        } else if (review.status === 'genuine') {
          stats.genuine++;
        }
      });

      return {
        success: true,
        stats
      };
    } catch (error) {
      console.error('[GroupReviews] Error getting stats:', error);
      return {
        success: false,
        error: error.message,
        stats: { total: 0, fake: 0, suspicious: 0, genuine: 0 }
      };
    }
  }

  /**
   * Bulk submit multiple reviews
   * @param {Array<Object>} reviews - Array of review data objects
   * @returns {Promise<Array<Object>>} - Array of submission responses
   */
  async submitBulkReviews(reviews) {
    const results = [];
    
    for (const review of reviews) {
      try {
        const result = await this.submitReview(review);
        results.push({ success: true, ...result });
      } catch (error) {
        results.push({ 
          success: false, 
          error: error.message,
          groupLink: review.groupLink 
        });
      }
    }
    
    return results;
  }

  /**
   * Get paginated reviews
   * @param {number} page - Page number (1-based)
   * @param {number} perPage - Items per page
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - Paginated results
   */
  async getPaginatedReviews(page = 1, perPage = 50, filters = {}) {
    const offset = (page - 1) * perPage;
    
    return await this.getReviews({
      ...filters,
      limit: perPage,
      offset
    });
  }
}

// Create singleton instance
const groupReviewsService = new GroupReviewsService();

export { groupReviewsService, GroupReviewsService };
export default groupReviewsService;
